/**
 * Hook for managing Acronis Topics loading based on credential blur events.
 *
 * Business Logic:
 * - Watches Base URL, Client ID, Client Secret from React Hook Form
 * - Validates credentials before calling API
 * - Calls Topics API only on credential field blur (not on keystroke)
 * - Prevents duplicate API calls for unchanged credentials
 * - Clears topics when Event Manager is removed from Category selection
 * - Supports both Create and Edit modes
 *
 * Key Design Decisions:
 * - Uses useWatch to monitor form values (no automatic triggers)
 * - Provides explicit blur handler for credential fields
 * - Tracks last successfully loaded credentials to prevent duplicates
 * - Returns topics as form-friendly array of strings (IDs or names based on API)
 * - Handles loading/error states for UI feedback
 */

import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { SourceDialogMode } from '../../SourceCreate.types';
import type {
  AcronisCredentialsState,
  AcronisFormValues,
  AcronisTopicOption,
  InFlightTopicsRequest,
  UseAcronisTopicsLoaderParams,
  UseAcronisTopicsLoaderReturn
} from '../AcronisTopics.types';
import { AcronisCategoryOption } from '../AcronisTopics.types';
import useAcronisTopicsApi from './useAcronisTopicsApi';

/**
 * Validates that all required credential fields have values.
 *
 * @param baseUrl - Base URL value
 * @param clientId - Client ID value
 * @param clientSecret - Client Secret value
 * @returns true if all credentials are filled, false otherwise
 */
const areCredentialsValid = (
  baseUrl: string | undefined,
  clientId: string | undefined,
  clientSecret: string | undefined
): boolean => {
  return Boolean(baseUrl?.trim() && clientId?.trim() && clientSecret?.trim());
};

const hasEventManagerCategory = (selectedCategories: unknown): boolean => {
  if (!Array.isArray(selectedCategories)) {
    return false;
  }

  return selectedCategories.some((category) => {
    if (typeof category === 'string') {
      return (
        category === AcronisCategoryOption.EventManager ||
        category === 'Event Manager'
      );
    }

    if (category && typeof category === 'object' && 'value' in category) {
      const categoryValue = (category as { value?: unknown }).value;
      return (
        typeof categoryValue === 'string' &&
        (categoryValue === AcronisCategoryOption.EventManager ||
          categoryValue === 'Event Manager')
      );
    }

    return false;
  });
};

const getCredentialsKey = (credentials: AcronisCredentialsState): string => {
  return JSON.stringify([
    credentials.base_url,
    credentials.client_id,
    credentials.client_secret
  ]);
};

/**
 * Custom hook for managing Acronis Topics loading with credential blur handlers.
 *
 * Usage in component:
 * ```tsx
 * const { topics, isLoading, error, handleCredentialBlur, clearTopics } =
 *   useAcronisTopicsLoader({ sourceTypeId });
 *
 * // In JSX for credential fields:
 * <TextField
 *   {...register('base_url')}
 *   onBlur={(event) => {
 *     baseUrlField.onBlur(event); // Call React Hook Form's onBlur
 *     handleCredentialBlur(); // Then trigger Topics API if needed
 *   }}
 * />
 * ```
 *
 * @param params - Hook configuration parameters
 * @returns Object containing topics, loading state, error, and handler functions
 */
export function useAcronisTopicsLoader(
  params: UseAcronisTopicsLoaderParams = {}
): UseAcronisTopicsLoaderReturn {
  const { getValues, trigger } = useFormContext<AcronisFormValues>();
  const { getTopics } = useAcronisTopicsApi();

  // Watch credential fields to keep values in sync (no automatic API trigger)
  const baseUrl = useWatch({ name: 'base_url' });
  const clientId = useWatch({ name: 'client_id' });
  const clientSecret = useWatch({ name: 'client_secret' });
  const selectedCategories = useWatch({ name: 'category' });

  // Track last successfully loaded credentials to prevent duplicates
  const lastLoadedCredentialsRef = useRef<AcronisCredentialsState | null>(null);
  const hasAttemptedEditBootstrapRef = useRef(false);
  const inFlightRequestsRef = useRef<Map<string, InFlightTopicsRequest>>(
    new Map()
  );
  const requestSequenceRef = useRef(0);
  const activeRequestIdRef = useRef(0);

  // Local state for topics, loading, and error
  const [topicsState, setTopicsState] = React.useState<AcronisTopicOption[]>(
    []
  );
  const [isLoadingState, setIsLoadingState] = React.useState(false);
  const [errorState, setErrorState] = React.useState<string | null>(null);
  const [
    hasSuccessfulTopicsResponseState,
    setHasSuccessfulTopicsResponseState
  ] = React.useState(false);

  // Mutation for Topics API call using TanStack Query
  const getTopicsMutation = useMutation<
    AcronisTopicOption[],
    Error,
    AcronisCredentialsState
  >({
    mutationFn: async (credentials: AcronisCredentialsState) => {
      const response = await getTopics({
        base_url: credentials.base_url,
        client_id: credentials.client_id,
        client_secret: credentials.client_secret
      });

      // Transform API response to form-friendly format
      // Filter topics to ensure id exists, then map with name fallback
      return response.topics
        .filter((topic) => topic.id)
        .map((topic) => ({
          id: topic.id,
          name: topic.name || topic.id
        }));
    }
  });

  /**
   * Handle credential field blur events.
   * Triggered explicitly when user leaves a credential field (after form blur handler).
   *
   * Steps:
   * 1. Validate all required credentials
   * 2. Check if credentials have changed since last load
   * 3. If valid and changed, call Topics API
   * 4. Show loading/error states during API call
   */
  const handleCredentialBlur = useCallback(async (): Promise<boolean> => {
    // Get current form values
    const currentBaseUrl = getValues('base_url');
    const currentClientId = getValues('client_id');
    const currentClientSecret = getValues('client_secret');
    const normalizedBaseUrl = String(currentBaseUrl ?? '').trim();
    const normalizedClientId = String(currentClientId ?? '').trim();
    const normalizedClientSecret = String(currentClientSecret ?? '').trim();

    // Validate that all credentials are filled
    if (
      !areCredentialsValid(
        normalizedBaseUrl,
        normalizedClientId,
        normalizedClientSecret
      )
    ) {
      activeRequestIdRef.current = ++requestSequenceRef.current;
      setTopicsState([]);
      setErrorState(null);
      setIsLoadingState(false);
      setHasSuccessfulTopicsResponseState(false);
      lastLoadedCredentialsRef.current = null;
      return false;
    }

    // First trigger React Hook Form validation for credential fields
    const isValidated = await trigger([
      'base_url',
      'client_id',
      'client_secret'
    ]);
    if (!isValidated) {
      // Validation failed, don't call API
      activeRequestIdRef.current = ++requestSequenceRef.current;
      setIsLoadingState(false);
      setHasSuccessfulTopicsResponseState(false);
      return false;
    }

    // Check if credentials have changed since last load
    const currentCredentials: AcronisCredentialsState = {
      base_url: normalizedBaseUrl,
      client_id: normalizedClientId,
      client_secret: normalizedClientSecret
    };
    const currentCredentialsKey = getCredentialsKey(currentCredentials);

    const hasPreviouslyLoaded = lastLoadedCredentialsRef.current;
    const credentialsUnchanged =
      hasPreviouslyLoaded &&
      hasPreviouslyLoaded.base_url === currentCredentials.base_url &&
      hasPreviouslyLoaded.client_id === currentCredentials.client_id &&
      hasPreviouslyLoaded.client_secret === currentCredentials.client_secret;

    // Skip API call if credentials haven't changed
    if (credentialsUnchanged) {
      return true;
    }

    const existingInFlightRequest = inFlightRequestsRef.current.get(
      currentCredentialsKey
    );

    if (existingInFlightRequest) {
      activeRequestIdRef.current = existingInFlightRequest.requestId;
      setIsLoadingState(true);
      setErrorState(null);
      setHasSuccessfulTopicsResponseState(false);

      try {
        await existingInFlightRequest.promise;
        return true;
      } catch {
        return false;
      }
    }

    // Call Topics API with current credentials
    const requestId = ++requestSequenceRef.current;
    activeRequestIdRef.current = requestId;
    setIsLoadingState(true);
    setErrorState(null);
    setHasSuccessfulTopicsResponseState(false);

    const requestPromise = getTopicsMutation.mutateAsync(currentCredentials);
    inFlightRequestsRef.current.set(currentCredentialsKey, {
      requestId,
      promise: requestPromise
    });

    try {
      const data = await requestPromise;

      if (activeRequestIdRef.current !== requestId) {
        return true;
      }

      lastLoadedCredentialsRef.current = currentCredentials;
      setTopicsState(data);
      setErrorState(null);
      setHasSuccessfulTopicsResponseState(true);
      setIsLoadingState(false);
      return true;
    } catch (err) {
      if (activeRequestIdRef.current !== requestId) {
        return false;
      }

      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load topics';
      setTopicsState([]);
      setErrorState(errorMessage);
      setHasSuccessfulTopicsResponseState(false);
      setIsLoadingState(false);
      lastLoadedCredentialsRef.current = null;
      return false;
    } finally {
      inFlightRequestsRef.current.delete(currentCredentialsKey);
    }
  }, [getValues, trigger, getTopicsMutation]);

  /**
   * Clear topics from form and state.
   * Called when Event Manager category is deselected.
   */
  const clearTopics = useCallback(() => {
    activeRequestIdRef.current = ++requestSequenceRef.current;
    setTopicsState([]);
    setErrorState(null);
    setIsLoadingState(false);
    setHasSuccessfulTopicsResponseState(false);
    lastLoadedCredentialsRef.current = null;
    hasAttemptedEditBootstrapRef.current = false;
  }, []);

  // Auto-clear topics when Event Manager is removed from categories
  React.useEffect(() => {
    if (!hasEventManagerCategory(selectedCategories)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      clearTopics();
    }
  }, [selectedCategories, clearTopics]);

  React.useEffect(() => {
    const shouldBootstrapEditTopics =
      params.mode === SourceDialogMode.Edit &&
      hasEventManagerCategory(selectedCategories) &&
      areCredentialsValid(baseUrl, clientId, clientSecret);

    if (!shouldBootstrapEditTopics || hasAttemptedEditBootstrapRef.current) {
      return;
    }

    hasAttemptedEditBootstrapRef.current = true;
    void handleCredentialBlur();
  }, [
    params.mode,
    selectedCategories,
    baseUrl,
    clientId,
    clientSecret,
    handleCredentialBlur
  ]);

  return {
    topics: topicsState,
    isLoading: isLoadingState,
    error: errorState,
    hasSuccessfulTopicsResponse: hasSuccessfulTopicsResponseState,
    handleCredentialBlur,
    clearTopics
  };
}
